import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../../components/feature/PageLayout';
import FeaturedSpotlight from './components/FeaturedSpotlight';
import { getCurrentEvent, getNextFeast, getDaysRemaining, getDaysUntil, getSeasonalArticlesForHome, type LiturgicalEvent } from '../../data/copticCalendar';
import { submitLocalForm } from '../../lib/localForms';

function SeasonalCountdown() {
  const { t } = useTranslation();
  const currentEvent = getCurrentEvent();
  const nextFeast = getNextFeast();

  const currentDays = currentEvent ? getDaysRemaining(currentEvent) : 0;
  const feastDays = nextFeast ? getDaysUntil(nextFeast) : 0;

  const currentIcon = currentEvent?.type === 'fast' ? 'ri-timer-line' : currentEvent?.type === 'holy-week' ? 'ri-cross-line' : 'ri-calendar-check-line';
  const nextIcon = 'ri-calendar-event-line';

  return (
    <div className="flex items-stretch justify-center gap-3 sm:gap-4 mb-10 flex-wrap">
      {/* Current Season Card */}
      {currentEvent && (
        <Link
          to="/news"
          className="group relative bg-white rounded-xl px-5 py-4 sm:px-6 sm:py-5 border border-stone-200 hover:border-burgundy-300 transition-all duration-300 text-center overflow-hidden cursor-pointer w-full sm:w-auto min-w-[220px] sm:min-w-[260px] shadow-sm flex flex-col justify-center"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-burgundy-300 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-burgundy-300/0 to-transparent group-hover:via-burgundy-300/30 transition-all duration-300"></div>
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <i className={`${currentIcon} text-burgundy-600 text-xs`}></i>
            <p className="text-[10px] sm:text-[11px] font-semibold text-burgundy-700 uppercase tracking-[0.12em]">
              {t('home.currentlyObserving')}
            </p>
          </div>
          <p className="text-xs font-bold text-stone-800 mb-2 leading-tight line-clamp-2">{currentEvent.name}</p>
          <div className="flex items-baseline justify-center gap-2 mb-1">
            <span className="text-3xl sm:text-4xl font-black text-burgundy-800 leading-none">{currentDays}</span>
            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
              {currentDays === 1 ? t('home.dayRemaining') : t('home.daysRemaining')}
            </span>
          </div>
          <p className="text-[10px] text-stone-400">{currentEvent.dateDisplay}</p>
        </Link>
      )}

      {/* Next Feast Card */}
      {nextFeast && (
        <Link
          to="/news"
          className="group relative bg-white rounded-xl px-5 py-4 sm:px-6 sm:py-5 border border-stone-200 hover:border-burgundy-300 transition-all duration-300 text-center overflow-hidden cursor-pointer w-full sm:w-auto min-w-[220px] sm:min-w-[260px] shadow-sm flex flex-col justify-center"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-400/0 to-transparent group-hover:via-amber-400/30 transition-all duration-300"></div>
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <i className={`${nextIcon} text-amber-600 text-xs`}></i>
            <p className="text-[10px] sm:text-[11px] font-semibold text-amber-700 uppercase tracking-[0.12em]">
              {t('home.nextFeast')}
            </p>
          </div>
          <p className="text-xs font-bold text-stone-800 mb-2 leading-tight line-clamp-2">{nextFeast.name}</p>
          <div className="flex items-baseline justify-center gap-2 mb-1">
            <span className="text-3xl sm:text-4xl font-black text-stone-800 leading-none">{feastDays}</span>
            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
              {feastDays === 1 ? t('home.dayAway') : t('home.daysAway')}
            </span>
          </div>
          <p className="text-[10px] text-stone-400">{nextFeast.dateDisplay}</p>
        </Link>
      )}
    </div>
  );
}

// Dynamic seasonal article cards powered by the Coptic liturgical calendar
// Shows current event article + paired feast if back-to-back, or next upcoming event if none active
function DynamicSeasonalArticle() {
  const { t } = useTranslation();
  const articles = getSeasonalArticlesForHome();
  if (articles.length === 0) return null;

  const currentEvent = getCurrentEvent();
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return (
    <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      {articles.map((article) => {
        const isActive = currentEvent?.id === article.eventId;
        const isFeast = article.type === 'feast';

        // Compute days badge
        let daysLabel = '';
        let daysIcon = 'ri-timer-line';
        if (isActive && currentEvent) {
          const remaining = getDaysRemaining(currentEvent);
          daysLabel = `${remaining} ${remaining === 1 ? t('home.dayRemaining') : t('home.daysRemaining')}`;
          daysIcon = 'ri-timer-line';
        } else {
          const start = new Date(article.startDate + 'T00:00:00');
          const daysUntilVal = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          daysLabel = `${daysUntilVal} ${daysUntilVal === 1 ? t('home.dayAway') : t('home.daysAway')}`;
          daysIcon = 'ri-calendar-event-line';
        }

        // Type label
        const typeLabel =
          article.type === 'fast'
            ? t('home.fastingSeason')
            : article.type === 'holy-week'
            ? t('home.holyWeek')
            : article.type === 'season'
            ? t('home.liturgicalSeason')
            : isFeast
            ? t('home.upcomingFeast')
            : t('home.currentSeason');

        // Badge styling
        const badgeStyle =
          article.type === 'fast'
            ? 'bg-sky-50/90 border-sky-300 text-sky-800'
            : article.type === 'holy-week'
            ? 'bg-rose-50/90 border-rose-300 text-rose-800'
            : isFeast
            ? 'bg-amber-50/90 border-amber-300 text-amber-800'
            : 'bg-amber-50/90 border-amber-300 text-amber-800';

        const badgeIcon =
          article.type === 'fast'
            ? 'ri-timer-line text-sky-500'
            : article.type === 'holy-week'
            ? 'ri-cross-line text-rose-500'
            : 'ri-calendar-check-line text-amber-500';

        return (
          <div
            key={article.eventId}
            className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all duration-300 shadow-sm flex flex-col"
          >
            <div className="flex flex-col h-full">
              {/* Image */}
              <div className="relative h-48 sm:h-56 overflow-hidden flex-shrink-0">
                <img
                  src={article.image}
                  alt={article.imageAlt}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>

                {/* Type badge */}
                <div className="absolute top-3 left-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest backdrop-blur-sm ${badgeStyle}`}
                  >
                    <i className={`${badgeIcon} text-xs`}></i>
                    {article.badge}
                  </span>
                </div>

              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest">{typeLabel}</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 text-stone-700 text-xs font-semibold">
                    <i className={`${daysIcon} text-xs`}></i>
                    {daysLabel}
                  </span>
                </div>

                {article.copticTitle && (
                  <p
                    className="font-serif italic text-sm text-burgundy-600 mb-1"
                    style={{ fontFamily: '"Crimson Text", serif' }}
                  >
                    {article.copticTitle}
                  </p>
                )}

                <h3
                  className="font-serif font-bold text-stone-800 text-lg sm:text-xl lg:text-2xl leading-snug mb-2"
                  style={{ fontFamily: '"Crimson Text", serif' }}
                >
                  {article.title}
                </h3>

                <p className="text-stone-400 text-xs font-medium mb-3">{article.dateRange}</p>

                <div className="h-0.5 w-10 rounded-full bg-burgundy-400/40 mb-4"></div>

                <p className="text-stone-500 text-sm leading-relaxed line-clamp-4 mb-5">{article.excerpt}</p>

                <Link
                  to={`/news?article=${article.eventId}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-burgundy-700 text-white text-xs font-semibold hover:bg-burgundy-800 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto justify-center"
                >
                  <i className="ri-newspaper-line text-xs"></i>
                  {t('home.readFullArticle')}
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Liturgy detail data keyed by service label
const LITURGY_DETAILS: Record<string, { language: string; location: string; description?: string }> = {
  'Wednesday Liturgy': {
    language: 'Arabic & English',
    location: 'Main Church',
    description: 'A mid-week Divine Liturgy offered in Arabic and English, open to all parishioners seeking spiritual nourishment.',
  },
  'Friday Liturgy': {
    language: 'Arabic & Coptic',
    location: 'Main Church',
    description: 'The Friday Divine Liturgy is celebrated in the ancient Coptic language alongside Arabic, preserving our apostolic heritage.',
  },
  'Saturday Liturgy': {
    language: 'English & French',
    location: 'Main Church',
    description: 'Saturday morning liturgy celebrated in English and French, welcoming our bilingual community.',
  },
  'Saturday Vespers & Midnight Praises': {
    language: 'All Languages',
    location: 'Main Church',
    description: 'An evening of Vespers and Midnight Praises in all languages of our community — a beautiful preparation for Sunday.',
  },
  'Sunday Liturgy': {
    language: 'All Languages',
    location: 'Main Church',
    description: 'The main Sunday Divine Liturgy celebrated in all languages, uniting our entire congregation in worship.',
  },
};

function getLiturgyDetails(dayName: string, title: string) {
  // Try matching by service title first
  for (const key of Object.keys(LITURGY_DETAILS)) {
    if (dayName.toLowerCase().includes(key.toLowerCase()) || title.toLowerCase().includes(key.toLowerCase())) {
      return LITURGY_DETAILS[key];
    }
  }
  return null;
}

// Liturgy Popup Modal
function LiturgyModal({
  item,
  onClose,
}: {
  item: { dayName: string; time: string; title: string } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!item) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [item, onClose]);

  if (!item) return null;

  const details = getLiturgyDetails(item.dayName, item.title);

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fadeIn"></div>
      <div
        className="relative z-10 w-full max-w-sm animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Card */}
        <div className="bg-[#1a0d0f] border border-amber-400/30 rounded-2xl overflow-hidden">
          {/* Gold top bar */}
          <div className="h-1 w-full bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>

          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-white/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold text-amber-300/70 uppercase tracking-widest mb-1">{t('home.divineService')}</p>
                <h3 className="text-xl font-serif font-bold text-white leading-tight" style={{ fontFamily: '"Crimson Text", serif' }}>
                  {item.dayName}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all cursor-pointer flex-shrink-0 mt-0.5"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            {/* Time */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-400/15 flex-shrink-0">
                <i className="ri-time-line text-amber-300 text-sm"></i>
              </div>
              <div>
                <p className="text-[10px] text-white/40 uppercase tracking-wider mb-0.5">{t('home.time')}</p>
                <p className="text-white font-semibold text-sm">{item.time}</p>
              </div>
            </div>

            {/* Language */}
            {details?.language && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-400/15 flex-shrink-0">
                  <i className="ri-translate-2 text-amber-300 text-sm"></i>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider mb-0.5">{t('home.language')}</p>
                  <p className="text-white font-semibold text-sm">{details.language}</p>
                </div>
              </div>
            )}

            {/* Location */}
            {details?.location && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-400/15 flex-shrink-0">
                  <i className="ri-map-pin-line text-amber-300 text-sm"></i>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider mb-0.5">{t('home.location')}</p>
                  <p className="text-white font-semibold text-sm">{details.location}</p>
                </div>
              </div>
            )}

            {/* Description */}
            {details?.description && (
              <p className="text-white/60 text-xs leading-relaxed pt-1 border-t border-white/10">
                {details.description}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-5">
            <Link
              to="/calendar"
              onClick={onClose}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full border border-amber-400/40 text-amber-300 text-xs font-semibold uppercase tracking-wider hover:bg-amber-400/10 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line text-sm"></i>
              {t('home.viewFullCalendar')}
            </Link>
          </div>

          {/* Gold bottom bar */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-amber-400/40 to-transparent"></div>
        </div>
      </div>
    </div>
  );
}

const GOOGLE_CALENDAR_ID = '30r61tr1g2f5loeqs3ulqtjfds@group.calendar.google.com';

type LiturgyEvent = {
  id: string;
  title: string;
  date: Date;
  endDate?: Date;
  time?: string;
  dayName: string;
};

function parseICalDate(dateStr: string): Date {
  if (dateStr.includes('T')) {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    const hour = parseInt(dateStr.substring(9, 11));
    const minute = parseInt(dateStr.substring(11, 13));
    return new Date(Date.UTC(year, month, day, hour, minute));
  }
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6)) - 1;
  const day = parseInt(dateStr.substring(6, 8));
  return new Date(year, month, day);
}

function isLiturgyEvent(title: string): boolean {
  const lower = title.toLowerCase();
  return lower.includes('liturgy') || lower.includes('mass') || lower.includes('divine liturgy');
}

const localSocialGallery = [
  {
    src: '/assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-768w.jpg',
    alt: 'Church sanctuary',
  },
  {
    src: '/assets/readdy/442a8abb-782f-432f-b6c4-09b7eb30eb08_IMG_0444-eafb70ea2d69.jpeg',
    alt: 'Church gathering',
  },
  {
    src: '/assets/readdy/55eb7943-6622-4ed4-a6b9-5e1f70851100_IMG_0507-e9374c281525.jpeg',
    alt: 'Church exterior',
  },
  {
    src: '/assets/readdy/a819e154-7847-4c75-899f-b7f02f0a9915_IMG_0518-0c77e3583d1e.jpeg',
    alt: 'Church community',
  },
  {
    src: '/assets/readdy/acf5d81a-7621-4035-b51f-5b78fc09c87e_IMG_0509-af3d7b64dee9.jpeg',
    alt: 'Church service',
  },
  {
    src: '/assets/readdy/ae412476-d8a4-4171-9f16-e652ac1aab98_IMG_0482-782cd519e7c7.jpeg',
    alt: 'Church event',
  },
];

// Newsletter Form Component
function NewsletterForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('firstName', firstName);

      const response = await submitLocalForm('Newsletter Signup', formData);

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setFirstName('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form 
      id="newsletter-form"
      data-local-form
      onSubmit={handleSubmit}
      className="max-w-lg mx-auto"
    >
      <div className="flex flex-col gap-3 md:gap-4">
        <input
          type="text"
          name="firstName"
          placeholder={t('home.firstName')}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="w-full px-4 py-3 md:py-3.5 rounded-full border border-gray-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-100 outline-none transition-all text-sm md:text-base"
          required
        />
        <input
          type="email"
          name="email"
          placeholder={t('home.emailAddress')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 md:py-3.5 rounded-full border border-gray-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-100 outline-none transition-all text-sm md:text-base"
          required
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-6 py-3 md:py-3.5 bg-burgundy-700 text-white text-sm md:text-base font-semibold rounded-full hover:bg-burgundy-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              {t('home.subscribing')}
            </span>
          ) : (
            t('home.subscribeBtn')
          )}
        </button>
      </div>

      {submitStatus === 'success' && (
        <div className="mt-4 p-3 md:p-4 bg-green-50 border border-green-200 rounded-xl text-center">
          <p className="text-green-700 text-sm md:text-base font-medium">
            <i className="ri-check-line mr-2"></i>
            {t('home.subscribeSuccess')}
          </p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mt-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-xl text-center">
          <p className="text-red-700 text-sm md:text-base font-medium">
            <i className="ri-error-warning-line mr-2"></i>
            {t('home.subscribeError')}
          </p>
        </div>
      )}

      <p className="text-center text-gray-500 text-xs mt-4">
        {t('home.privacyNote')}
      </p>
    </form>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [videos, setVideos] = useState<any[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [activeVideo, setActiveVideo] = useState<{ id: string; title: string } | null>(null);
  const [liturgyEvents, setLiturgyEvents] = useState<LiturgyEvent[]>([]);
  const [loadingLiturgy, setLoadingLiturgy] = useState(true);

  // Compute the current week's date range
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const startOfWeek = new Date();
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);
    startOfWeek.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  }, []);

  useEffect(() => {
    const fetchLiturgy = async () => {
      setLoadingLiturgy(true);
      try {
        const icalUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(GOOGLE_CALENDAR_ID)}/public/basic.ics`;
        let proxyUrl = `https://corsproxy.io/?${encodeURIComponent(icalUrl)}`;
        let response = await fetch(proxyUrl);
        if (!response.ok) {
          proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(icalUrl)}`;
          response = await fetch(proxyUrl);
        }
        if (response.ok) {
          const icalData = await response.text();
          const lines = icalData.split('\n');
          const parsed: LiturgyEvent[] = [];
          let cur: Partial<LiturgyEvent & { rawDate?: Date; rawEnd?: Date }> = {};
          let inEvent = false;

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (line === 'BEGIN:VEVENT') { inEvent = true; cur = {}; }
            else if (line === 'END:VEVENT' && inEvent) {
              if (cur.title && cur.rawDate && isLiturgyEvent(cur.title)) {
                const startTime = cur.rawDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                const endTime = cur.rawEnd ? cur.rawEnd.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
                parsed.push({
                  id: cur.id || Math.random().toString(),
                  title: cur.title,
                  date: cur.rawDate,
                  endDate: cur.rawEnd,
                  time: endTime ? `${startTime} – ${endTime}` : startTime,
                  dayName: cur.rawDate.toLocaleDateString('en-US', { weekday: 'long' }),
                });
              }
              inEvent = false;
            } else if (inEvent) {
              if (line.startsWith('SUMMARY:')) cur.title = line.substring(8).trim();
              else if (line.startsWith('DTSTART')) {
                const ds = line.split(':')[1]?.trim();
                if (ds) cur.rawDate = parseICalDate(ds);
              } else if (line.startsWith('DTEND')) {
                const ds = line.split(':')[1]?.trim();
                if (ds) cur.rawEnd = parseICalDate(ds);
              } else if (line.startsWith('UID:')) cur.id = line.substring(4).trim();
            }
          }

          // Filter to current week only
          const weekStart = weekDays[0];
          const weekEnd = new Date(weekDays[6]);
          weekEnd.setHours(23, 59, 59, 999);

          const thisWeek = parsed
            .filter(e => e.date >= weekStart && e.date <= weekEnd)
            .sort((a, b) => a.date.getTime() - b.date.getTime());

          setLiturgyEvents(thisWeek);
        } else {
          setLiturgyEvents([]);
        }
      } catch {
        setLiturgyEvents([]);
      } finally {
        setLoadingLiturgy(false);
      }
    };
    fetchLiturgy();
  }, [weekDays]);

  const CHANNEL_ID = 'UCQJ9Z8Z8Z8Z8Z8Z8Z8Z8Z8Z';
  const API_KEY = 'AIzaSyCW_BcbpVi2SjK43I2jYN0e0IOyrlYJ45I';

  useEffect(() => {
    fetchChannelVideos();
  }, []);

  const fetchChannelVideos = async () => {
    try {
      const channelResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&forHandle=SMPCVI&key=${API_KEY}`
      );
      const channelData = await channelResponse.json();
      if (channelData.items && channelData.items.length > 0) {
        const uploadsPlaylistId = channelData.items[0].contentDetails.relatedPlaylists.uploads;
        const videosResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=10&playlistId=${uploadsPlaylistId}&key=${API_KEY}`
        );
        const videosData = await videosResponse.json();
        if (videosData.items) {
          const regularVideos = videosData.items.filter((video: any) => {
            const title = video.snippet.title.toLowerCase();
            const description = video.snippet.description.toLowerCase();
            return !title.includes('live stream') && !title.includes('livestream') && !title.includes('🔴') && !description.startsWith('live stream');
          }).slice(0, 3);
          setVideos(regularVideos);
        }
      }
      setLoadingVideos(false);
    } catch (error) {
      console.error('Error fetching channel videos:', error);
      setLoadingVideos(false);
    }
  };

  const openVideo = (videoId: string, title: string) => {
    setActiveVideo({ id: videoId, title });
  };

  const closeVideo = useCallback(() => {
    setActiveVideo(null);
  }, []);

  useEffect(() => {
    if (!activeVideo) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeVideo(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [activeVideo, closeVideo]);

  // Automated schedule from data file
  const [selectedLiturgy, setSelectedLiturgy] = useState<{ dayName: string; time: string; title: string } | null>(null);
  const [autoSchedule, setAutoSchedule] = useState<ReturnType<typeof import('../../data/schedule').getWeekSchedule>>([]);
  
  useEffect(() => {
    import('../../data/schedule').then(({ getWeekSchedule }) => {
      setAutoSchedule(getWeekSchedule());
    });
  }, []);

  const scheduleToShow = liturgyEvents.length > 0
    ? liturgyEvents.map(e => ({ dayName: e.dayName, time: e.time || '', icon: '', title: e.title }))
    : autoSchedule.map(s => {
        const serviceLabel = s.service.replace(' Liturgy', '').replace(' & Midnight Praises', '');
        const isVespers = s.service.includes('Vespers');
        const label = isVespers
          ? `${s.day} Vespers & Midnight Praises`
          : `${s.day} Liturgy`;
        return {
          dayName: s.note ? `${s.day} (${s.note})` : label,
          time: s.time,
          icon: '',
          title: isVespers ? 'Vespers & Midnight Praises' : 'Divine Liturgy',
          _serviceLabel: serviceLabel,
        };
      });

  return (
    <PageLayout hasHero>
      {/* Liturgy Detail Modal */}
      <LiturgyModal item={selectedLiturgy} onClose={() => setSelectedLiturgy(null)} />

      {/* Video Player Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
          onClick={closeVideo}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm animate-fadeIn"></div>
          <div 
            className="relative z-10 w-full max-w-5xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeVideo}
              className="absolute -top-12 right-0 flex items-center gap-2 text-white/80 hover:text-white transition-colors cursor-pointer group"
            >
              <span className="text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">{t('home.close')}</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <i className="ri-close-line text-xl"></i>
              </div>
            </button>

            <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-4">
              <h3 className="text-white font-semibold text-lg line-clamp-2 flex-1">{activeVideo.title}</h3>
              <a
                href={`https://www.youtube.com/watch?v=${activeVideo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-youtube-line text-base"></i>
                {t('home.openOnYouTube')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20 md:py-0">
        <div className="absolute inset-0">
          <picture>
            <source
              type="image/webp"
              sizes="100vw"
              srcSet="/assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-768w.webp 768w, /assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-1280w.webp 1280w, /assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-1920w.webp 1920w, /assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-2560w.webp 2560w"
            />
            <img
              src="/assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-1920w.jpg"
              sizes="100vw"
              srcSet="/assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-768w.jpg 768w, /assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-1280w.jpg 1280w, /assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-1920w.jpg 1920w, /assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e-2560w.jpg 2560w"
              alt="Saint Mina & Pope Cyril VI Coptic Orthodox Church Interior"
              width={5732}
              height={3821}
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto w-full pt-16 md:pt-0">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-serif font-bold text-white mb-4 md:mb-6 leading-tight">
            {t('hero.title')}<br />{t('hero.subtitle')}
          </h1>
          <div className="mb-6 md:mb-10 flex flex-col items-center gap-2">
            <p className="font-serif italic text-xl sm:text-2xl md:text-3xl text-white/95 tracking-wide" style={{fontFamily: '"Crimson Text", serif'}}>
              {t('hero.tagline')}
            </p>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-amber-400/60"></span>
              <p className="text-xs sm:text-sm text-amber-300/90 uppercase tracking-[0.25em] font-medium">
                {t('hero.location')}
              </p>
              <span className="w-8 h-px bg-amber-400/60"></span>
            </div>
          </div>

          {/* Liturgy Schedule — live from Google Calendar */}
          <div className="mb-8 md:mb-12">
            <p className="text-xs text-white/60 uppercase tracking-widest mb-3 font-medium flex items-center justify-center gap-2">
              <i className="ri-calendar-check-line"></i>
              {loadingLiturgy ? t('home.loadingLiturgySchedule') : liturgyEvents.length > 0 ? t('home.thisWeekLiturgies') : t('home.thisWeekSchedule')}
            </p>

            {loadingLiturgy ? (
              <div className="flex justify-center gap-3 flex-wrap">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-5 border border-white/20 animate-pulse w-36 h-20"></div>
                ))}
              </div>
            ) : (
              <div className={`grid gap-2 sm:gap-3 ${scheduleToShow.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : scheduleToShow.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'}`}>
                {scheduleToShow.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedLiturgy({ dayName: item.dayName, time: item.time, title: item.title })}
                    className="group relative bg-white/10 backdrop-blur-md rounded-xl px-4 py-4 sm:py-5 border border-white/20 hover:border-amber-400/60 hover:bg-white/15 transition-all duration-300 text-center overflow-hidden cursor-pointer w-full"
                  >
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"></div>
                    <p className={`text-[10px] sm:text-[11px] font-semibold text-amber-300/80 uppercase tracking-[0.12em] mb-2 leading-tight ${item.dayName.toLowerCase().includes('vespers') ? 'break-words' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`}>{item.dayName}</p>
                    <div className="w-6 h-px bg-amber-400/40 mx-auto mb-2"></div>
                    <p className="text-sm sm:text-base font-bold text-white tracking-wide">{item.time}</p>
                    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-400/0 to-transparent group-hover:via-amber-400/30 transition-all duration-300"></div>
                    <i className="ri-information-line absolute top-2 right-2 text-[10px] text-white/30 group-hover:text-amber-300/60 transition-colors"></i>
                  </button>
                ))}
              </div>
            )}

            {liturgyEvents.length > 0 && (
              <p className="text-[10px] text-white/50 mt-2 flex items-center justify-center gap-1">
                <i className="ri-google-fill text-xs"></i>
                {t('home.liveFromGoogleCalendar')}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link to="/calendar" className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-transparent border-2 border-white text-white text-sm sm:text-base font-medium rounded-full hover:bg-white hover:text-burgundy-900 transition-all whitespace-nowrap cursor-pointer">
              {t('home.viewFullCalendar')}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <i className="ri-arrow-down-line text-2xl md:text-3xl text-gold-400/70"></i>
        </div>
      </section>

      {/* Featured Spotlight — Feast / Fast / Holy Week / Event */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-3" style={{ fontFamily: '"Crimson Text", serif' }}>
              {t('home.seasonalPosts')}
            </h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm md:text-base">
              {t('home.seasonalPostsDesc')}
            </p>
          </div>
          <SeasonalCountdown />
          <DynamicSeasonalArticle />
          <FeaturedSpotlight />
          <div className="text-center mt-8 md:mt-10">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-burgundy-700 text-white text-sm sm:text-base font-semibold rounded-full hover:bg-burgundy-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-newspaper-line"></i>
              {t('home.viewAllNews')}
            </Link>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#722f37] to-[#5a252c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-xs sm:text-sm text-gold-400 mb-2 md:mb-3 uppercase tracking-wider font-semibold">{t('home.stayConnected')}</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-3 md:mb-4">
              {t('home.followUsSocial')}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8">
              {t('home.socialDesc')}
            </p>


          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {localSocialGallery.map((item) => (
              <a
                key={item.src}
                href="https://www.instagram.com/smpcvi/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group block aspect-square overflow-hidden rounded-xl border border-white/15 bg-white/10 cursor-pointer"
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </a>
            ))}
          </div>

          {/* Social Media Link Buttons — below the posts */}
          <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/smpcvi"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/15 hover:bg-[#1877F2]/80 border border-white/30 text-white font-medium text-sm transition-all cursor-pointer backdrop-blur-sm hover:border-[#1877F2] whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-facebook-fill text-lg"></i>
              </div>
              Facebook
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/smpcvi/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/15 hover:bg-gradient-to-r hover:from-[#833ab4] hover:via-[#fd1d1d] hover:to-[#fcb045] border border-white/30 text-white font-medium text-sm transition-all cursor-pointer backdrop-blur-sm whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-instagram-line text-lg"></i>
              </div>
              Instagram
            </a>

            {/* YouTube */}
            <a
              href="https://www.youtube.com/@SMPCVI"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/15 hover:bg-[#FF0000]/80 border border-white/30 text-white font-medium text-sm transition-all cursor-pointer backdrop-blur-sm hover:border-[#FF0000] whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-youtube-fill text-lg"></i>
              </div>
              YouTube
            </a>

            {/* Website */}
            <a
              href="https://smpcvi.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/15 hover:bg-white/30 border border-white/30 text-white font-medium text-sm transition-all cursor-pointer backdrop-blur-sm whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-global-line text-lg"></i>
              </div>
              Website
            </a>
          </div>
        </div>
      </section>

      {/* Sermons Section — moved after Instagram */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-xs sm:text-sm text-burgundy-700 mb-2 md:mb-3 uppercase tracking-wider font-semibold">{t('home.spiritualGuidance')}</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-3 md:mb-4">
              {t('home.liveStreaming')}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.sermonsDesc')}
            </p>
          </div>

          {loadingVideos ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-stone-50 rounded-xl md:rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-300"></div>
                  <div className="p-4 md:p-5">
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : videos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {videos.map((video, index) => {
                  const videoId = video.snippet.resourceId.videoId;
                  const thumbnail = video.snippet.thumbnails.high.url;
                  const title = video.snippet.title;
                  const publishedAt = new Date(video.snippet.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });

                  return (
                    <div
                      key={videoId}
                      onClick={() => openVideo(videoId, title)}
                      className="bg-stone-50 rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={thumbnail}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/90 group-hover:scale-110 transition-transform">
                            <i className="ri-play-fill text-2xl text-burgundy-700 ml-1"></i>
                          </div>
                        </div>
                        {index === 0 && (
                          <span className="absolute top-3 left-3 px-2 py-1 bg-gold-500 text-burgundy-900 text-xs font-bold rounded">
                            {t('home.latest')}
                          </span>
                        )}
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-burgundy-700 transition-colors">
                          {title}
                        </h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{publishedAt}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center mt-8 md:mt-10">
                <a
                  href="https://www.youtube.com/@SMPCVI/videos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 bg-burgundy-700 text-white text-sm sm:text-base font-semibold rounded-full hover:bg-burgundy-800 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-youtube-line"></i>
                  {t('home.viewAllOnYouTube')}
                </a>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('home.noSermonsAvailable')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Our Coptic Orthodox Church Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-3 md:mb-4">{t('home.ourChurch')}</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.churchLeadersDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            <div className="bg-stone-50 rounded-2xl md:rounded-3xl overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 sm:h-56 md:h-64 overflow-hidden">
                <img 
                  src="/assets/readdy/ed2190d4522749c3675b16fa11c4cf46-e2711b899710.png"
                  alt="His Holiness Pope Tawadros II"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('leaders.popeTawadros')}</h3>
                <p className="text-xs sm:text-sm text-burgundy-700 font-medium mb-2 md:mb-3">{t('leaders.popeTawadrosTitle')}</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed hidden sm:block">
                  {t('leaders.popeTawadrosDesc')}
                </p>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl md:rounded-3xl overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 sm:h-56 md:h-64 overflow-hidden">
                <img 
                  src="/assets/readdy/b073ceb25798d0e74cb37f9a44eef647-f69c779c3ae5.png"
                  alt="His Grace Bishop Boulos"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('leaders.bishopBoulos')}</h3>
                <p className="text-xs sm:text-sm text-burgundy-700 font-medium mb-2 md:mb-3">{t('leaders.bishopBoulosTitle')}</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed hidden sm:block">
                  {t('leaders.bishopBoulosDesc')}
                </p>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl md:rounded-3xl overflow-hidden hover:shadow-xl transition-all sm:col-span-2 md:col-span-1">
              <div className="h-48 sm:h-56 md:h-64 overflow-hidden">
                <img 
                  src="/assets/readdy/f46f13081ecf4f52070ca15241409afa-e78d0b782d54.png"
                  alt="Father Anthony Micheal"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('leaders.fatherAnthony')}</h3>
                <p className="text-xs sm:text-sm text-burgundy-700 font-medium mb-2 md:mb-3">{t('leaders.fatherAnthonyTitle')}</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed hidden sm:block">
                  {t('leaders.fatherAnthonyDesc')}
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-8 md:mt-12">
            <Link to="/about" className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-burgundy-700 text-white text-sm sm:text-base font-semibold rounded-full hover:bg-burgundy-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-right-line"></i>
              {t('home.learnMoreAboutUs')}
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 md:py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 shadow-lg border border-gray-100">
            <div className="text-center mb-6 md:mb-8">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 md:mb-6 flex items-center justify-center bg-gold-100 rounded-full">
                <i className="ri-mail-send-line text-2xl md:text-3xl text-burgundy-700"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-3 md:mb-4">
                {t('home.stayConnectedNewsletter')}
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-xl mx-auto">
                {t('home.subscribeNewsletter')}
              </p>
            </div>

            <NewsletterForm />

            {/* Monthly Newsletter Preview */}
            <div className="mt-8 md:mt-10 pt-8 md:pt-10 border-t border-gray-100">
              <h3 className="text-center text-lg md:text-xl font-serif font-bold text-gray-900 mb-4 md:mb-6">
                {t('home.monthlyNewsletter')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {[
                  { id: 1, titleKey: 'home.newsletter.january', dateKey: 'home.newsletter.janDate', descKey: 'home.newsletter.janDesc' },
                  { id: 2, titleKey: 'home.newsletter.december', dateKey: 'home.newsletter.decDate', descKey: 'home.newsletter.decDesc' },
                  { id: 3, titleKey: 'home.newsletter.november', dateKey: 'home.newsletter.novDate', descKey: 'home.newsletter.novDesc' },
                  { id: 4, titleKey: 'home.newsletter.october', dateKey: 'home.newsletter.octDate', descKey: 'home.newsletter.octDesc' },
                ].map((nl) => (
                  <div key={nl.id} className="bg-stone-50 rounded-xl overflow-hidden hover:shadow-md transition-all group cursor-pointer">
                    <div className="h-32 w-full overflow-hidden bg-stone-100">
                      <img
                        src="/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png"
                        alt={t(nl.titleKey)}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] text-amber-600 font-medium mb-1">{t(nl.dateKey)}</p>
                      <h4 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-burgundy-700 transition-colors line-clamp-1">{t(nl.titleKey)}</h4>
                      <p className="text-xs text-gray-500 line-clamp-2">{t(nl.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4 md:mt-6">
                <Link
                  to="/news#newsletters"
                  className="inline-flex items-center gap-1.5 text-sm text-burgundy-700 hover:text-burgundy-800 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-newspaper-line"></i>
                  {t('home.viewAllNewsletters')}
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section — Services */}
      <section className="py-12 md:py-20 bg-burgundy-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white mb-4 md:mb-6">{t('home.ourServices')}</h2>
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto">
            {t('home.servicesDesc')}
          </p>
          <Link to="/services" className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-gold-500 text-burgundy-900 text-sm sm:text-base font-semibold rounded-full hover:bg-gold-400 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
            <i className="ri-heart-line"></i>
            {t('home.exploreServices')}
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
